import { z } from 'zod';
import type { ReportKind, ReportMode, ReportTemplateBody, ReportTemplateDto, ReportVerify } from './teacher-reports.js';

/**
 * Teacher report answers and scoring — shared by the portal (live points
 * while filling) and the API (the authoritative score at submit). Keeping
 * one implementation means the points a teacher watches are the points they
 * get.
 */

export const STUDENT_TIERS = ['top', 'medium', 'average'] as const;
export type StudentTier = (typeof STUDENT_TIERS)[number];

/**
 * The groups on a report: the three the portal suggests, plus students with
 * nothing to go on yet (no marks, quizzes or attendance) until the teacher
 * places them.
 */
export const GROUP_KEYS = ['top', 'medium', 'average', 'unplaced'] as const;
export type GroupKey = (typeof GROUP_KEYS)[number];
export const GROUP_LABEL: Record<GroupKey, string> = {
  top: 'Top',
  medium: 'Medium',
  average: 'Average',
  unplaced: 'Not in a group yet',
};

export const SUPPORT_STRATEGIES = ['in_class', 'whatsapp', 'extra_time', 'online_class', 'parent_call'] as const;
export type SupportStrategy = (typeof SUPPORT_STRATEGIES)[number];
export const SUPPORT_STRATEGY_LABEL: Record<SupportStrategy, string> = {
  in_class: 'In class',
  whatsapp: 'WhatsApp',
  extra_time: 'Extra time',
  online_class: 'Online class',
  parent_call: 'Parent call',
};

const tierAnswerSchema = z
  .object({
    strategies: z.array(z.enum(SUPPORT_STRATEGIES)).max(SUPPORT_STRATEGIES.length),
    note: z.string().trim().max(500).optional(),
    problem: z.string().trim().max(200).optional(),
    subject: z.string().trim().max(200).optional(),
  })
  .strict();
export type TierAnswer = z.infer<typeof tierAnswerSchema>;

/**
 * What the teacher filled in. Item values: `true` for a ticked `check`, a
 * number for a `counter`. `auto`, `share` and `upload` values are ignored —
 * the server decides those from portal data and recorded events.
 */
export const reportAnswersSchema = z
  .object({
    items: z.record(z.string().max(48), z.union([z.boolean(), z.number().int().min(0).max(999)])),
    groups: z
      .object({
        top: tierAnswerSchema.optional(),
        medium: tierAnswerSchema.optional(),
        average: tierAnswerSchema.optional(),
        unplaced: tierAnswerSchema.optional(),
      })
      .strict(),
    fields: z.record(z.string().max(48), z.union([z.string().max(4000), z.array(z.string().uuid()).max(100)])),
    /**
     * The teacher's own grouping for this report: student id → group. A
     * student who isn't listed stays where the portal suggested.
     */
    placements: z
      .record(z.string().uuid(), z.enum(STUDENT_TIERS))
      .refine((r) => Object.keys(r).length <= 200, 'Too many students')
      .optional(),
  })
  .strict();
export type ReportAnswers = z.infer<typeof reportAnswersSchema>;

export const EMPTY_ANSWERS: ReportAnswers = { items: {}, groups: {}, fields: {} };

/** Portal facts that decide `auto`, `share`, `upload` and `showWhen` items. */
export interface ScoringFacts {
  verified: Partial<Record<ReportVerify, boolean>>;
  /** Item keys with a recorded share / upload event. */
  events: readonly string[];
  /**
   * Items that don't apply this time (no one absent, no exam this month…).
   * They're shown as "not needed" and their points are awarded, so a
   * teacher's maximum doesn't swing day to day.
   */
  notApplicable: readonly string[];
  /** Students per suggested group (students with nothing to go on aren't counted). */
  tierCounts: Record<StudentTier, number>;
  /**
   * The report's students and their suggested group; `score: null` means
   * nothing to go on yet. With the teacher's placements this decides who is
   * in which group. Older facts may not carry it.
   */
  students?: ReadonlyArray<{ id: string; tier: StudentTier; score: number | null }>;
}

export interface ScoreLine {
  key: 'base' | 'items' | 'groups' | 'reflect';
  label: string;
  points: number;
  max: number;
}

export interface ReportScore {
  lines: ScoreLine[];
  total: number;
  max: number;
  itemsDone: number;
  itemsCount: number;
  /** Per checklist item: done, and why when it wasn't the teacher's tick. */
  items: Array<{ key: string; done: boolean; points: number; source: 'teacher' | 'portal' | 'event' | 'not_applicable' }>;
  groupsDone: boolean;
  reflectDone: boolean;
}

/**
 * Each student's group on this report: where the teacher put them, else the
 * portal's suggestion; a student with nothing to go on starts "not in a group
 * yet".
 */
export function placeStudents(
  facts: Pick<ScoringFacts, 'students'>,
  answers: Pick<ReportAnswers, 'placements'>,
): Map<string, GroupKey> {
  const out = new Map<string, GroupKey>();
  for (const st of facts.students ?? []) {
    out.set(st.id, answers.placements?.[st.id] ?? (st.score === null ? 'unplaced' : st.tier));
  }
  return out;
}

/** Students in each group on this report. Older facts without a student list use the suggested counts. */
export function groupCounts(
  facts: Pick<ScoringFacts, 'students' | 'tierCounts'>,
  answers: Pick<ReportAnswers, 'placements'>,
): Record<GroupKey, number> {
  if (!facts.students) return { ...facts.tierCounts, unplaced: 0 };
  const counts: Record<GroupKey, number> = { top: 0, medium: 0, average: 0, unplaced: 0 };
  for (const g of placeStudents(facts, answers).values()) counts[g] += 1;
  return counts;
}

/**
 * Groups the teacher must answer: only those with students in them. With no
 * students at all there's nothing to answer, and the group points are given.
 */
export const requiredGroups = (counts: Record<GroupKey, number>): GroupKey[] => GROUP_KEYS.filter((g) => counts[g] > 0);

/**
 * The answers as they're kept on submit: nothing for groups nobody is in (an
 * answer left behind after the teacher moved everyone out), and no
 * placements for students who aren't on this report.
 */
export function cleanAnswers(answers: ReportAnswers, facts: Pick<ScoringFacts, 'students' | 'tierCounts'>): ReportAnswers {
  const onReport = new Set((facts.students ?? []).map((st) => st.id));
  const placements = Object.fromEntries(Object.entries(answers.placements ?? {}).filter(([id]) => onReport.has(id)));
  const placed: ReportAnswers = { items: answers.items, groups: answers.groups, fields: answers.fields };
  if (Object.keys(placements).length > 0) placed.placements = placements;
  const counts = groupCounts(facts, placed);
  const groups: ReportAnswers['groups'] = {};
  for (const g of GROUP_KEYS) {
    const a = answers.groups[g];
    if (a && counts[g] > 0) groups[g] = a;
  }
  return { ...placed, groups };
}

export const tierComplete = (
  answer: TierAnswer | undefined,
  template: Pick<ReportTemplateBody, 'groups'>,
): boolean =>
  !!answer &&
  answer.strategies.length > 0 &&
  (!template.groups.askProblemAndSubject || !!answer.problem?.trim());

export const REFLECTION_MIN_CHARS = 10;

export function scoreReport(input: {
  template: Pick<ReportTemplateBody, 'basePoints' | 'groupsPoints' | 'reflectPoints' | 'items' | 'fields' | 'groups'>;
  answers: ReportAnswers;
  facts: ScoringFacts;
  late: boolean;
}): ReportScore {
  const { template: t, answers, facts, late } = input;
  const events = new Set(facts.events);
  const notApplicable = new Set(facts.notApplicable);

  const items = t.items.map((item) => {
    if (notApplicable.has(item.key)) return { key: item.key, done: true, points: item.points, source: 'not_applicable' as const };
    let done = false;
    let source: 'teacher' | 'portal' | 'event' = 'teacher';
    switch (item.type) {
      case 'check':
        done = answers.items[item.key] === true;
        break;
      case 'counter':
        done = Number(answers.items[item.key] ?? 0) >= (item.target ?? 1);
        break;
      case 'auto':
        source = 'portal';
        done = item.verify ? facts.verified[item.verify] === true : false;
        break;
      case 'share':
      case 'upload':
        source = 'event';
        done = events.has(item.key);
        break;
    }
    return { key: item.key, done, points: done ? item.points : 0, source };
  });
  const itemsMax = t.items.reduce((sum, i) => sum + i.points, 0);
  const itemsPoints = items.reduce((sum, i) => sum + i.points, 0);
  const itemsDone = items.filter((i) => i.done).length;

  const groupsDone = requiredGroups(groupCounts(facts, answers)).every((g) => tierComplete(answers.groups[g], t));
  const reflectFields = t.fields.filter((f) => f.reflect);
  const reflectDone =
    reflectFields.length > 0 &&
    reflectFields.every((f) => {
      const v = answers.fields[f.key];
      return typeof v === 'string' && v.trim().length >= REFLECTION_MIN_CHARS;
    });

  const lines: ScoreLine[] = [
    { key: 'base', label: late ? 'Submitted late — no on-time points' : 'Submitted on time', points: late ? 0 : t.basePoints, max: t.basePoints },
    { key: 'items', label: `Checklist · ${itemsDone}/${t.items.length} done`, points: itemsPoints, max: itemsMax },
    { key: 'groups', label: 'Student groups', points: groupsDone ? t.groupsPoints : 0, max: t.groupsPoints },
  ];
  if (t.reflectPoints > 0) {
    lines.push({ key: 'reflect', label: 'Reflections', points: reflectDone ? t.reflectPoints : 0, max: t.reflectPoints });
  }
  return {
    lines,
    total: lines.reduce((s, l) => s + l.points, 0),
    max: lines.reduce((s, l) => s + l.max, 0),
    itemsDone,
    itemsCount: t.items.length,
    items,
    groupsDone,
    reflectDone,
  };
}

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------

export type TeacherReportStatus = 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'missed' | 'excused';

export interface ReportMetricDto {
  key: string;
  label: string;
  value: string;
  source: string;
}

export interface ReportStudentDto {
  id: string;
  name: string;
  /** 0–100, or null when there's nothing to go on yet. */
  score: number | null;
  scoreSource: 'exams' | 'quizzes' | 'attendance' | null;
  tier: StudentTier;
}

export interface ReportFactsDto extends ScoringFacts {
  metrics: ReportMetricDto[];
  /** Plain-words evidence for portal-checked items, e.g. "Marked at 5:48 PM". */
  verifiedDetail: Partial<Record<ReportVerify, string>>;
  notApplicableReason: Record<string, string>;
  students: ReportStudentDto[];
  absentStudents: string[];
  eventsDetail: Record<string, { at: string; fileName?: string }>;
}

export interface TeacherReportDto {
  id: string | null;
  status: TeacherReportStatus | null;
  answers: ReportAnswers;
  pointsTotal: number | null;
  pointsLines: ScoreLine[] | null;
  isLate: boolean;
  submittedAt: string | null;
  reviewNote: string | null;
  resubmitBy: string | null;
  templateVersion: number;
}

export interface ReportFormResponse {
  now: string;
  kind: ReportKind;
  mode: ReportMode;
  batchId: string;
  batchName: string;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  window: {
    status: 'locked' | 'open' | 'late' | 'closed';
    opensAt: string;
    dueAt: string;
    graceUntil: string;
    opensBecause: string;
  } | null;
  /** Submitting now is allowed (open, late, or changes requested in time). */
  canSubmit: boolean;
  template: ReportTemplateDto;
  facts: ReportFactsDto;
  report: TeacherReportDto;
  /** The report's optional voice note, if one was recorded. */
  voiceNote: VoiceNoteDto | null;
  /** How long voice notes are kept, for the notice under the recorder. */
  voiceNoteRetentionDays: number;
}

export interface VoiceNoteDto {
  uploadedAt: string;
  durationSec: number | null;
  sizeBytes: number;
  /** When the audio will be deleted (null once it has been). */
  deletesOn: string | null;
  /** The audio was deleted after the retention period. */
  deleted: boolean;
}

export interface ReportSubmitResponse {
  report: TeacherReportDto;
  score: ReportScore;
  late: boolean;
  streak: { current: number; nextBonusIn: number; bonusEarned: number };
  pendingPointsThisMonth: number;
}

export interface ReportShareResponse {
  message: string;
  url: string;
}

export interface MyReportHistoryItem {
  id: string;
  kind: ReportKind;
  mode: ReportMode;
  batchId: string;
  batchName: string;
  periodKey: string;
  periodStart: string;
  status: TeacherReportStatus;
  isLate: boolean;
  pointsTotal: number | null;
  submittedAt: string | null;
  reviewNote: string | null;
  resubmitBy: string | null;
}
